// Refined-product markets (DESIGN §19.1). Same recipe as the crude market (§6.1):
//   price(day) = keyframe(product, yearFrac) × newsMult(day) × exp(noise + beta·glut)
//   noise: OU, 0.985·noise + N(0, σ_product); glut = clamp(log(crude spot / crude base), −2, 1)
// Kerosene is much steadier than crude — that steadiness is the refiner's edge. Weekly history like crude.
import { PRODUCT_PRICES, PRODUCT_NOISE, M1_PRODUCTS, REFINING_NEWS, EXPORT_PREMIUM, OCEAN_FREIGHT, EXPORT_VOYAGE_DAYS,
  FREIGHT_INDEX, PRODUCT_DEPTH_GAL, DEPTH_SHARE, GAL_PER_BBL, WAX_LB_PER_GAL, PRODUCTS, DEPTH_SHARE_BY_YEAR, REFINED_PRODUCTS, productsAt } from '../data/refining.js';
import { yearFrac, dateToDay } from '../core/time.js';
import { basePriceForDay } from './market.js'; // after the product tables end, refined prices follow crude
import { OIL_PRICES } from '../data/oilPrices.js';
// annual crude means at mid-year, the same placement as the product tables (for the 2000+ crack ratio)
const CRUDE_KEYS = Object.entries(OIL_PRICES).map(([y, v]) => [+y + 0.5, +v]).sort((a, b) => a[0] - b[0]);
import { normal, subRng } from '../core/rng.js';

export const HISTORY_EVERY_DAYS = 7;
const M1_SET = new Set(M1_PRODUCTS);
export const MAX_HISTORY = 3000;
const OU_PHI = 0.985;

// ---------------------------------------------------------------- interpolation helpers
// pts: [[x, y], ...] sorted; linear, clamped at both ends.
export function interp(pts, x) {
  if (!pts?.length) return 0;
  if (x <= pts[0][0]) return pts[0][1];
  const last = pts[pts.length - 1];
  if (x >= last[0]) return last[1];
  let lo = 0, hi = pts.length - 1;
  while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (pts[mid][0] <= x) lo = mid; else hi = mid; }
  const [x0, y0] = pts[lo], [x1, y1] = pts[hi];
  return y0 + (y1 - y0) * (x - x0) / (x1 - x0);
}

// Annual means placed at mid-year (as market.js does for crude).
const KEYS = Object.fromEntries(Object.entries(PRODUCT_PRICES).map(([p, rows]) => [p, rows.map(([y, v]) => [y + 0.5, v])]));

export const exportPremium = (year) => interp(EXPORT_PREMIUM, year);
export const oceanFreight = (year) => interp(OCEAN_FREIGHT, year);
export const voyageDays = (year) => Math.round(interp(EXPORT_VOYAGE_DAYS, year));
export const freightIndex = (year) => interp(FREIGHT_INDEX, year);
// Gallons/day (lb for wax) of a product the player can sell into one market before the price sags.
export const productDepth = (pid, year) => interp(PRODUCT_DEPTH_GAL, year) * (DEPTH_SHARE_BY_YEAR[pid] ? interp(DEPTH_SHARE_BY_YEAR[pid], year) : DEPTH_SHARE[pid] ?? 0.1);
// Average price multiplier for selling q more units today when q0 were already sold (same law as crude, production.js).
export function depthMult(D, q0, q) {
  if (!(D > 0)) return 1;
  if (q <= 1e-9) return 1 / (1 + q0 / D);
  return (D / q) * (Math.log(1 + (q0 + q) / D) - Math.log(1 + q0 / D));
}

// ---------------------------------------------------------------- deterministic base
const PRICE_EVENTS = REFINING_NEWS.filter((n) => n.effect?.type === 'productPrice');

// Multiplier from refining news on `day` (full at the event, fading linearly over effect.days).
export function newsMult(pid, day) {
  let m = 1;
  for (const n of PRICE_EVENTS) {
    if (n.effect.product !== pid) continue;
    const age = day - n.day;
    if (age >= 0 && age < n.effect.days) m *= 1 + (n.effect.mult - 1) * (1 - age / n.effect.days);
  }
  return m;
}

// Deterministic, noise-free price on `day` (nominal $ per gal / lb). Defined for every day. Past the last keyframe
// it moves with the crude base price (so refining margins survive into years the tables don't cover) instead of
// freezing — a frozen 1999 price under 2010s crude is what broke refinery margins before the 2000s rows existed.
export function productBasePrice(pid, day) {
  const k = KEYS[pid];
  if (!k) return 0;
  const t = yearFrac(day), last = k[k.length - 1];
  let v = interp(k, t);
  if (t > last[0]) { const d0 = dateToDay(Math.floor(last[0]), 7, 1), c0 = basePriceForDay(d0); if (c0 > 0) v *= Math.max(0.2, basePriceForDay(day) / c0); }
  else if (t >= 2000) { // tables are annual means; crude's base path is monthly — keep the year's product/crude ratio day by day
    const ca = interp(CRUDE_KEYS, t), cd = basePriceForDay(day);
    if (ca > 0 && cd > 0) v *= Math.min(2.5, Math.max(0.4, cd / ca));
  }
  return v * newsMult(pid, day);
}

// ---------------------------------------------------------------- state
// Stale after a date jump (Quick Start, tests) → resync prices to today's base; history is kept.
export const STALE_DAYS = 7;
export function initProductMarkets(state) {
  const old = state.productMarkets;
  if (old && old.kerosene && Math.abs((old.day ?? state.day) - state.day) <= STALE_DAYS) return old;
  if (old && old.kerosene) {
    for (const pid of productsAt(yearFrac(state.day))) { const m = (old[pid] ??= { history: [] }); m.base = m.price = round(productBasePrice(pid, state.day)); m.noise = 0; m.history ??= []; }
    old.day = state.day;
    return old;
  }
  const pm = { lastHistoryDay: state.day, day: state.day };
  for (const pid of productsAt(yearFrac(state.day))) {
    const p = round(productBasePrice(pid, state.day));
    pm[pid] = { price: p, base: p, noise: 0, history: [[state.day, p]] };
  }
  state.productMarkets = pm;
  return pm;
}

// Crude glut term: how far crude spot sits from its own base (both from state.market).
function crudeGlut(state) {
  const m = state.market;
  if (!m || !(m.spot > 0) || !(m.base > 0)) return 0;
  return Math.max(-2, Math.min(1, Math.log(m.spot / m.base)));
}

const PASS_2000 = { lube: 0.6, wax: 0.6, asphalt: 0.6 };

// Daily update (call after tickMarket so the crude spot is today's).
export function tickProductMarkets(state) {
  const pm = initProductMarkets(state);
  pm.day = state.day;
  const glut = crudeGlut(state);
  const writeHistory = state.day - (pm.lastHistoryDay ?? -1e9) >= HISTORY_EVERY_DAYS;
  for (const pid of productsAt(yearFrac(state.day))) { // M2/M3 products join on their market year
    const m = (pm[pid] ??= { price: 0, base: 0, noise: 0, history: [] });
    const nz = PRODUCT_NOISE[pid] ?? { sigma: 0.01, beta: 0.1 };
    // M1 cuts draw from the game rng (unchanged since M1); later cuts use a derived per-day stream so adding a
    // product market never shifts the main random sequence (determinism of everything else).
    m.noise = OU_PHI * m.noise + (M1_SET.has(pid) ? normal(state, 0, nz.sigma) : normal(subRng(state.seed ?? 0, `pm:${pid}:${state.day}`), 0, nz.sigma));
    m.base = productBasePrice(pid, state.day);
    // From 2000 product markets track crude closely (cracks mean-revert): fuels pass ~90% of crude's swing through,
    // specialty cuts ~60%. Earlier eras keep their looser coupling (and their RNG / goldens).
    const beta = yearFrac(state.day) >= 2000 ? Math.max(nz.beta, PASS_2000[pid] ?? 0.9) : nz.beta;
    m.price = m.base * Math.exp(m.noise + beta * glut);
    if (writeHistory) {
      m.history.push([state.day, round(m.price)]);
      if (m.history.length > MAX_HISTORY) thin(m.history);
    }
  }
  if (writeHistory) pm.lastHistoryDay = state.day;
  return pm;
}

// Halve the resolution of the oldest half of a history (keeps saves small in very long games).
function thin(h) {
  const half = Math.floor(h.length / 2);
  const old = h.slice(0, half).filter((_, i) => i % 2 === 0);
  h.splice(0, half, ...old);
}

export function productPrice(state, pid) {
  const pm = state.productMarkets;
  const fresh = pm && Math.abs((pm.day ?? state.day) - state.day) <= STALE_DAYS;
  return (fresh ? pm?.[pid]?.price : null) ?? productBasePrice(pid, state.day);
}

export function productPrices(state) {
  return Object.fromEntries(productsAt(yearFrac(state.day)).map((p) => [p, productPrice(state, p)]));
}

// ---------------------------------------------------------------- crack spread
// Value of the products from ONE barrel of crude at the given prices, minus the crude price.
// yields: fractions of crude volume per product (wax converted gal→lb). prices: $/gal (wax $/lb).
// Returns { value, crude, spread, byProduct: { pid: $ } }.
export function crackSpread(yields, prices, crudePrice) {
  const byProduct = {};
  let value = 0;
  for (const pid of REFINED_PRODUCTS) {
    const f = yields?.[pid] ?? 0;
    if (!f) continue;
    const units = f * GAL_PER_BBL * (pid === 'wax' ? WAX_LB_PER_GAL : 1);
    const v = units * (prices[pid] ?? 0);
    byProduct[pid] = v;
    value += v;
  }
  return { value, crude: crudePrice, spread: value - crudePrice, byProduct };
}

// Market-level crack spread on `state.day`: Penn Grade on a batch still at NY prices vs crude spot.
export function marketCrackSpread(state, yields) {
  return crackSpread(yields, productPrices(state), state.market?.spot ?? 0);
}

// Annual keyframe table for charts before there is in-game history.
export function annualSeries(pid, fromYear = 1859, toYear = 1999) {
  return (PRODUCT_PRICES[pid] ?? []).filter(([y]) => y >= fromYear && y <= toYear);
}

export const productUnit = (pid) => PRODUCTS[pid]?.unit ?? 'gal';
const round = (p) => Math.round(p * 10000) / 10000;
